import { Stack } from '@mui/material';
import tokens from '@verifime/design-tokens';
import React from 'react';
import { Control, FieldErrorsImpl, FieldValues, UseFormSetError } from 'react-hook-form';
import ResponsiveBox from '@componentsroot/ResponsiveBox';
import { TFieldsOperations, TFromFieldInfo } from '@componentsroot/Form/FormGenerator/utils';
import { fieldComponentMapping } from '@componentsroot/Form/FormGenerator/common/mapping';
import { TFormFieldsAndRules } from '@componentsroot/Form/FormGenerator/v3/utils';

/**
 *
 * As requested, duplicate of `GenerateFormFields_New` but supporting RowContainer for each row,
 * to avoid breaking changes, and to keep the old one for a while
 * until we are sure that the new one is working as expected.
 */
export default function GenerateFormFieldsV3<TFieldValues extends FieldValues = FieldValues>({
  formFieldsAndRules,
  control,
  errors,
  setError,
  formFieldsWithDefaultValues,
  fieldsOperations,
  defaultMargin = 'normal',
  defaultSize = 'medium',
}: Readonly<{
  formFieldsAndRules: TFormFieldsAndRules<keyof TFieldValues>;
  control: Control<TFieldValues, any>;
  setError?: UseFormSetError<TFieldValues>;
  errors: Partial<FieldErrorsImpl<Record<keyof TFieldValues, any>>>;
  formFieldsWithDefaultValues?: Record<string, any>;
  /**
   *
   * TODO: remove this the functionality has covered by fieldInfo.onValueChange
   *
   * There are maybe several Select to be rendered in the `formFieldsAndRules`,
   * each of them may have different `initial value`, `onOptionChange` operations,
   * and those `onOptionChange` even `initial value` cannot be predefined in the `formFieldsAndRules`,
   * thus expose this to accept different operations of fields
   */
  fieldsOperations?: TFieldsOperations<TFieldValues>;
  defaultSize?: TFromFieldInfo['size'];
  defaultMargin?: TFromFieldInfo['margin'];
}>) {
  if (Object.keys(formFieldsAndRules || {}).length < 1) {
    return null;
  }

  return (
    <Stack direction="column" sx={{ gap: tokens.spacingXs }}>
      {Object.entries(formFieldsAndRules).map(
        ([rowNumber, { RowContainer = ResponsiveBox, fields }]) => {
          if (!fields || fields.length < 1) {
            return null;
          }
          const fieldsInfoWithDefaults = fields.map((fieldInfo) => ({
            ...fieldInfo,
            size: fieldInfo.size ?? defaultSize,
            margin: fieldInfo.margin ?? defaultMargin,
          }));
          return (
            <RowContainer key={rowNumber}>
              {fieldsInfoWithDefaults.map((fieldInfo) => {
                const {
                  initValue,
                  onOptionChange,
                  onFileChange,
                  disabled,
                  onTextChange,
                  onDateChange,
                  onDateRangeChange,
                  onCheckBoxChange,
                  onRadioChange,
                  onNumberChange,
                } = fieldsOperations?.[fieldInfo.fieldName] || {};
                return (
                  <React.Fragment key={String(fieldInfo.fieldName)}>
                    {fieldInfo.renderComponent
                      ? fieldInfo.renderComponent()
                      : fieldComponentMapping[fieldInfo.renderType]?.({
                          control,
                          setError,
                          errors,
                          fieldInfo,
                          formFieldsWithDefaultValues,
                          onOptionChange,
                          initValue,
                          onFileChange,
                          disabled,
                          onTextChange,
                          onDateChange,
                          onDateRangeChange,
                          onCheckBoxChange,
                          onRadioChange,
                          onNumberChange,
                        })}
                  </React.Fragment>
                );
              })}
            </RowContainer>
          );
        },
      )}
    </Stack>
  );
}
